<template>
  <modal
    ref="modal"
    class="price-form"
    playground
    :title="$t('title')"
    @hidden="onHidden"
    @shown="focusPrimaryInput"
  >
    <form @submit.prevent="submit">
      <div class="uk-modal-body">
        <validation-alert-box :errors="fields.errors.base" />

        <organization-combobox
          :label="$t('fields.organization')"
          v-model="fields.values.organization"
          :errors="fields.errors.organization"
          :placeholder="$t('placeholders.defaultOrganization')"
          :disabled="!isEditable"
        />

        <radio-group-input
          :label="$t('fields.type')"
          v-model="fields.values.type"
          :errors="fields.errors.type"
          :collection="availablePriceTypes"
          :disabled="!isEditable"
        />

        <radio-group-input
          :label="$t('fields.paymentFrequency')"
          v-model="fields.values.paymentFrequency"
          :errors="fields.errors.paymentFrequency"
          :collection="availablePaymentFrequencies"
          :disabled="!isEditable"
        />

        <money-input
          ref="primaryInput"
          :label="$t('fields.value')"
          size="large"
          class="uk-width-1-4"
          v-model.number="fields.values.value"
          :errors="fields.errors.value"
          :disabled="!isEditable"
        />
      </div>

      <div class="uk-modal-footer uk-text-right">
        <submit-button type="primary" :disabled="!isSubmittable" />
        <common-button @click="hide">
          {{$t('actions.cancel')}}
        </common-button>
      </div>
    </form>
  </modal>
</template>

<script>
import CommonButton from '@/components/CommonButton';
import Form from '@/mixins/Form';
import Modal from '@/components/Modal';
import MoneyInput from '@/components/MoneyInput';
import OrganizationCombobox from '@/components/OrganizationCombobox';
import RadioGroupInput from '@/components/RadioGroupInput';
import SubmitButton from '@/components/SubmitButton';
import ValidationAlertBox from '@/components/ValidationAlertBox';

const PAYMENT_FREQUENCIES = ['ONETIME', 'MONTHLY'];
const PRICE_TYPES = ['DEFAULT', 'WAS'];

export default {
  name: 'price-form',
  components: {
    CommonButton,
    Modal,
    MoneyInput,
    OrganizationCombobox,
    RadioGroupInput,
    SubmitButton,
    ValidationAlertBox,
  },
  mixins: [
    Form({
      base: {
        loadFrom: false,
        dumpTo: false,
        validate: [
          function validatePriceNotExists(value, field, values) {
            const orgId = values.organization && values.organization.id;
            const otherPrice = this.otherPrices.find(price =>
              price.paymentFrequency === values.paymentFrequency &&
              price.type === values.type &&
              (
                (price.organization && price.organization.id === orgId) ||
                (!price.organization && !orgId)
              ),
            );

            return {
              isValid: !otherPrice,
              message: this.$t('validationErrors.priceTaken'),
            };
          },
        ],
      },
      // Do not just submit with the ID but with the entire org object as we
      // want to show the org in the price table.
      organization: true,
      type: { default: PRICE_TYPES[0] },
      paymentFrequency: { default: PAYMENT_FREQUENCIES[0] },
      value: { required: true },
    }),
  ],
  props: {
    prices: Array,
  },
  computed: {
    id() {
      if (!this.values) return null;
      return this.values.id;
    },
    availablePriceTypes() {
      return PRICE_TYPES.map(type => ({
        label: this.$t(`priceTypes.${type.toLowerCase()}`),
        value: type,
      }));
    },
    availablePaymentFrequencies() {
      return PAYMENT_FREQUENCIES.map(frequency => ({
        label: this.$t(`paymentFrequencies.${frequency.toLowerCase()}`),
        value: frequency,
      }));
    },
    otherPrices() {
      return this.prices.filter(price => price.id !== this.id);
    },
  },
  methods: {
    show() {
      this.$refs.modal.show();
    },
    hide() {
      this.$refs.modal.hide();
    },
    onHidden() {
      this.resetToDefaults();
      this.$emit('hidden');
    },
    focusPrimaryInput() {
      this.$refs.primaryInput.focus();
    },
    resetToDefaults() {
      this.fields.values.organization = null;
      this.fields.values.type = 'DEFAULT';
      this.fields.values.paymentFrequency = 'ONETIME';
      this.fields.values.value = null;
      this.fields.errors.base = [];
      this.fields.errors.organization = [];
      this.fields.errors.type = [];
      this.fields.errors.paymentFrequency = [];
      this.fields.errors.value = [];
    },
  },
  i18n: {
    messages: {
      de: {
        title: 'Preis verwalten',
        fields: {
          type: 'Preistyp',
          organization: 'Vertriebsorganisation',
          paymentFrequency: 'Zahlungsintervall',
          value: 'Preis',
        },
        paymentFrequencies: {
          onetime: 'einmalig',
          monthly: 'monatlich',
        },
        placeholders: {
          defaultOrganization: 'Standard (alle)',
        },
        priceTypes: {
          default: 'Normalpreis',
          was: 'Streichpreis',
        },
        validationErrors: {
          priceTaken: 'Der Preis ist bereits vergeben',
        },
      },
    },
  },
};
</script>



// WEBPACK FOOTER //
// PriceForm.vue?0c115513